import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { Page } from "../../../activities/AppPage";
import { STRINGS } from "../../../strings";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { translate } from "../../../translate";
import { onHasNotch } from "../../../utils/DeviceUtils";
import { onIntentToDismissLeft } from "../../../utils/framerUtils";
import { getFullName } from "../../../utils/getFullName";
import {
  AdjustmentsHorizontal20SolidIcon,
  CardAccountDetailsOutlineIcon,
  CashStackIcon,
  GiftOutlineIcon,
  HandHeartIcon,
  LearningApp20RegularIcon,
  PersonRoundedIcon,
  SettingsOutlineIcon,
  WrappedGiftIcon,
} from "../../../utils/icones";
import { AppButton } from "../../Generic/AppButton";
import AsyncImage from "../../Generic/AsyncImage";
import useFlag from "../hooks/useFlag";
import { getCaregiverPhotoUrl } from "../main-utils";

type Props = {
  isOpen: boolean;
  caregiver: AppCaregiver;
  onRequestClose: () => void;
  onRequestNavigate: (page: Page) => void;
};

export default function Sidebar(props: Props) {
  const { isFlagged: hasOpenedCareMoments } = useFlag({ name: "OpenedCareMomentsIntro" });

  const items: {
    icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    label: string;
    page: Page;
    isHidden?: boolean;
  }[] = [
    {
      icon: HandHeartIcon,
      label: translate(STRINGS.CARE_MOMENTS),
      page: { type: hasOpenedCareMoments ? "CareMoments" : "CareMomentsIntro" },
      isHidden: !props.caregiver.shouldShowCareMoments,
    },
    {
      icon: GiftOutlineIcon,
      label: translate(STRINGS.MEDFLYT_RAFFLE),
      page: { type: "Raffle" },
      isHidden: !props.caregiver.shouldShowRaffle,
    },
    {
      icon: CashStackIcon,
      label: translate(STRINGS.PAYROLL),
      page: { type: "CaregiverPaystubList", caregiver: props.caregiver },
    },
    {
      icon: CardAccountDetailsOutlineIcon,
      label: translate(STRINGS.COMPLIANCE),
      page: { type: "MyMedflytCompliance" },
      isHidden: !props.caregiver.shouldShowMyMedflyCompliance,
    },
    {
      icon: LearningApp20RegularIcon,
      label: translate(STRINGS.INSERVICES),
      page: { type: "TrainingCenter" },
    },
    {
      icon: AdjustmentsHorizontal20SolidIcon,
      label: translate(STRINGS.CASE_SETTINGS),
      page: {
        type: "MedflytCaseSettings",
        showMyStafingPreferences: props.caregiver.shouldShowMyStaffingPreferences,
      },
    },
    {
      icon: SettingsOutlineIcon,
      label: translate(STRINGS.SETTINGS_AND_INFO),
      page: { type: "Settings" },
    },
  ];

  const handleClickItem = (page: Page) => {
    props.onRequestClose();
    props.onRequestNavigate(page);
  };

  return (
    <AnimatePresence>
      {props.isOpen && (
        <Root>
          <Wrapper
            initial={{ translateX: "-100%" }}
            animate={{ translateX: 0 }}
            exit={{ translateX: "-100%" }}
            transition={{ duration: 0.3, ease: [0.1, 0.7, 0.1, 1] }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={{ left: 1, right: 0.001 }}
            onPanEnd={onIntentToDismissLeft(props.onRequestClose)}
          >
            <SidebarContent
              caregiver={props.caregiver}
              items={items.filter((i) => i.isHidden !== true)}
              onNavigate={handleClickItem}
              onRequestNavigate={props.onRequestNavigate}
            />
          </Wrapper>
          <Dimmer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={props.onRequestClose}
          />
        </Root>
      )}
    </AnimatePresence>
  );
}

type SidebarContentProps = {
  caregiver: AppCaregiver;
  items: Array<{
    icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    label: string;
    page: Page;
  }>;
  onNavigate: (page: Page) => void;
  onRequestNavigate: (page: Page) => void;
};

function SidebarContent(props: SidebarContentProps) {
  const { caregiver, items, onNavigate, onRequestNavigate } = props;

  return (
    <>
      <HeaderCard>
        <ProfileSection>
          <Avatar src={getCaregiverPhotoUrl(caregiver)} />
          <ProfileInfo>
            <CaregiverName>{getFullName(caregiver)}</CaregiverName>
            <PointsDisplay onClick={() => onRequestNavigate({ type: "CarebackPoints", caregiver })}>
              <WrappedGiftIcon color="var(--tw-yellow-500)" fontSize={20} />
              {caregiver.shouldShowCaregiverRating && (
                <PointsText>{caregiver.points} points</PointsText>
              )}
            </PointsDisplay>
          </ProfileInfo>
        </ProfileSection>
        <ProfileButton onClick={() => onRequestNavigate({ type: "Profile" })}>
          <PersonRoundedIcon fontSize={18} />
          <span>{translate(STRINGS.MY_PROFILE)}</span>
        </ProfileButton>
      </HeaderCard>

      <MenuContainer>
        {items.map((item, index) => (
          <MenuItem
            key={item.label}
            icon={item.icon}
            label={item.label}
            onClick={() => onNavigate(item.page)}
            isFirst={index === 0}
            isLast={index === items.length - 1}
          />
        ))}
      </MenuContainer>
    </>
  );
}

function MenuItem(props: {
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  label: string;
  onClick: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}) {
  return (
    <MenuItemRoot onClick={props.onClick} isFirst={props.isFirst} isLast={props.isLast}>
      <MenuIconWrapper>
        <props.icon />
      </MenuIconWrapper>
      <span>{props.label}</span>
    </MenuItemRoot>
  );
}

// Styled components
const Root = styled(motion.nav)`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 2;
  display: flex;
`;

const Dimmer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(20, 30, 40, 0.7);
  backdrop-filter: blur(3px);
`;

const Wrapper = styled(motion.div)`
  height: 100%;
  width: 80%;
  background: #fff;
  z-index: 1;
  display: flex;
  flex-direction: column;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
`;

const HeaderCard = styled.div`
  background: linear-gradient(to right, var(--tw-blue-500), var(--tw-blue-600));
  padding: 24px 16px 16px;
  color: white;
  position: relative;
  flex-shrink: 0;

  ${onHasNotch(
    () => css`
      padding-top: calc(env(safe-area-inset-top) + 24px);
    `
  )}
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Avatar = styled(AsyncImage)`
  background-color: var(--tw-slate-200);
  --size: 60px;
  width: var(--size);
  height: var(--size);
  background-size: cover;
  border-radius: 12px;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const CaregiverName = styled.div`
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
  color: white;
`;

const PointsDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 4px 0;
`;

const PointsText = styled.span`
  font-size: 14px;
  font-weight: 600;
  opacity: 0.9;
`;

const ProfileButton = styled(AppButton)`
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 16px;
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  width: 100%;
  justify-content: center;
  gap: 8px;

  &:hover,
  &:active {
    background-color: rgba(255, 255, 255, 0.3);
  }
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
  background-color: white;
  flex: 1;
  overflow-y: auto;
`;

const MenuIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background-color: var(--tw-slate-100);
`;

const MenuItemRoot = styled.div<{ isFirst?: boolean; isLast?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  margin: 0 8px;
  border-radius: 8px;

  ${(props) =>
    props.isFirst === true &&
    css`
      margin-top: 8px;
    `}

  ${(props) =>
    props.isLast === true &&
    css`
      margin-bottom: 8px;
    `}

  &:hover {
    background-color: var(--tw-slate-100);
  }

  &:active {
    background-color: var(--tw-slate-200);
  }

  span {
    font-weight: 600;
    font-size: 15px;
    line-height: 20px;
    color: var(--tw-slate-700);
  }

  svg {
    color: var(--tw-blue-500);
    font-size: 20px;
  }
`;
